import { Alerts, Button, Field, Input, Text, Textarea } from '@components/ui';
import { ChangePasswordDialog } from '@features/profile/change-password-dialog';
import { errorBody } from '@lib/api/http';
import {
  useDeleteAccountMutation,
  useProfileQuery,
  useUpdateProfileMutation,
} from '@lib/api/profile';
import { uploadFile } from '@lib/api/uploads';
import useLogout from '@lib/auth/useLogout';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { Buffer } from 'buffer';
import { type ChangeEvent, useEffect, useState } from 'react';
import { validate } from 'react-email-validator';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import type {
  ProfileLocationState,
  ProfileMutationResponse,
  ProfileResponse,
  ProfileUpdate,
} from './profileInformationTypes';
const browserWindow = window as Window & { Buffer?: typeof Buffer };
browserWindow.Buffer = browserWindow.Buffer || Buffer;

const ProfileInformation = (): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const updateProfile = useUpdateProfileMutation();
  const deleteAccount = useDeleteAccountMutation();
  const massage = (location.state as ProfileLocationState | null)?.massage;
  const [isReadonly, setIsReadonly] = useState(true);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [, setMobileNumber] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [bio, setBio] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [userProfilePic, setUserProfilePic] = useState<string | undefined>();
  const [userProfile, setUserProfile] = useState<string | undefined>();
  const [imageUploading, setImageUploading] = useState(false);
  const logout = useLogout();

  const { data: profileResult = {} } = useProfileQuery();
  const userDetails = profileResult as ProfileResponse;

  useEffect(() => {
    if (massage) {
      toast.error(massage);
    }
  }, [massage]);

  useEffect(() => {
    const user = userDetails.data?.user;
    if (user == null) return;
    if (
      user.firstName == null ||
      user.firstName === '' ||
      user.email == null ||
      user.email === ''
    ) {
      setIsReadonly(false);
    }
    setFirstName(userDetails.data?.user?.firstName ?? null);
    setMobileNumber(userDetails.data?.user?.mobileNumber ?? null);
    setEmail(userDetails.data?.user?.email ?? null);
    setCompanyName(userDetails.data?.user?.companyName ?? null);
    setBio(userDetails.data?.user?.bio ?? null);
    setUserProfilePic(userDetails.data?.user?.avatar);
  }, [userDetails]);

  const handlefirstNameChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    setFirstName(event.target.value);
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value);
  };

  const handleCompanyNameChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    setCompanyName(event.target.value);
  };

  const handleBioChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setBio(event.target.value);
  };

  const savedUser = userDetails.data?.user;
  const hasChanges =
    (firstName ?? '') !== (savedUser?.firstName ?? '') ||
    (email ?? '') !== (savedUser?.email ?? '') ||
    (companyName ?? '') !== (savedUser?.companyName ?? '') ||
    (bio ?? '') !== (savedUser?.bio ?? '') ||
    Boolean(userProfile);

  const resetFormToSaved = (): void => {
    setFirstName(savedUser?.firstName ?? null);
    setMobileNumber(savedUser?.mobileNumber ?? null);
    setEmail(savedUser?.email ?? null);
    setCompanyName(savedUser?.companyName ?? null);
    setBio(savedUser?.bio ?? null);
    setUserProfilePic(savedUser?.avatar);
    setUserProfile(undefined);
  };

  const handleFormEnabled = (): void => {
    if (!isReadonly) {
      resetFormToSaved();
    }
    setIsReadonly(!isReadonly);
  };

  const handleResponse = (data: ProfileMutationResponse): void => {
    if (data.status) {
      setIsReadonly(true);
      setLoading(false);
      setUserProfile(undefined);
      toast.success(data.message);
    } else {
      setLoading(false);
      toast.error(data.message);
    }
  };
  const handleUploadImage = async (file: File): Promise<string | undefined> => {
    try {
      setImageUploading(true);
      const uploadRes = await uploadFile(file, 'profilePics');
      setImageUploading(false);
      return uploadRes.location;
    } catch (error: unknown) {
      setImageUploading(false);
      return;
    }
  };
  const handleSubmit = (): void => {
    const emailValid = validate(email ?? '');
    if (!emailValid) {
      toast.error('Please add a valid email');
      return;
    }
    setLoading(true);
    const data = {
      firstName,
      email,
      companyName: companyName ?? undefined,
      bio: bio ?? undefined,
      avatar: userProfile,
    } as ProfileUpdate;
    updateProfile.mutate(data, {
      onSuccess: (result) => handleResponse(result as ProfileMutationResponse),
      onError: (error: unknown) => handleResponse(errorBody(error)),
    });
  };

  const handleProfilePic = async (
    event: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const files = event.target.files as FileList;
    const url = URL.createObjectURL(files[0]);
    setUserProfilePic(url);
    const latestImagesUrls = await handleUploadImage(files[0]);
    setUserProfile(latestImagesUrls);
  };

  const DeleteUserAccountActionRes = (): void => {
    logout();
  };

  const handleDeleteVideosRes = (): void => {
    deleteAccount.mutate(undefined, {
      onSuccess: DeleteUserAccountActionRes,
    });
  };

  const handleDeleteAccount = (): void => {
    Alerts.deleteAccountAlert(handleDeleteVideosRes);
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1120,
        mx: 'auto',
        px: { xs: 2, md: 4 },
        py: 3,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        {userDetails.data?.user?.firstName ? (
          <Button
            type="button"
            variant="outlined"
            size="sm"
            onClick={handleFormEnabled}
          >
            {isReadonly ? t('common.edit') : t('common.cancel')}
          </Button>
        ) : null}
      </Box>
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <Box
        component="form"
        onSubmit={(event) => event.preventDefault()}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box
          sx={{
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: { xs: 2.5, md: 4 },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              component="input"
              type="file"
              id="profile"
              accept="image/*"
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                void handleProfilePic(event);
              }}
              disabled={isReadonly}
              sx={{ display: 'none' }}
            />
            {userProfilePic ? (
              <Box
                component="img"
                src={userProfilePic}
                alt={t('profile.photoAlt')}
                sx={{
                  height: 96,
                  width: 96,
                  borderRadius: '50%',
                  border: '3px solid',
                  borderColor: 'divider',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  height: 96,
                  width: 96,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: '3px solid',
                  borderColor: 'divider',
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? '#262a2e' : '#f7f8fa',
                }}
              />
            )}
            {!isReadonly && (
              <IconButton
                type="button"
                aria-label={t('profile.changePhoto')}
                onClick={() => {
                  const profileInput = document.getElementById(
                    'profile'
                  ) as HTMLInputElement;
                  profileInput.click();
                }}
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  left: 'calc(50% + 22px)',
                  zIndex: 1,
                  width: 34,
                  height: 34,
                  border: 2,
                  borderColor: 'background.paper',
                  bgcolor: 'text.primary',
                  color: 'background.paper',
                  '&:hover': { bgcolor: 'text.secondary' },
                }}
              >
                <PhotoCameraOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              columnGap: 2.5,
              rowGap: 0.5,
            }}
          >
            <Field label={t('profile.name')}>
              <Input
                type="text"
                value={firstName ?? ''}
                name="firstName"
                onChange={handlefirstNameChange}
                disabled={isReadonly}
                placeholder={t('profile.namePlaceholder')}
              />
            </Field>

            <Field label={t('profile.mobile')}>
              <Input
                type="text"
                value={userDetails.data?.user?.mobileNumber ?? ''}
                disabled
              />
            </Field>

            <Field label={t('profile.company')}>
              <Input
                type="text"
                value={companyName ?? ''}
                name="companyName"
                onChange={handleCompanyNameChange}
                disabled={isReadonly}
                placeholder={t('profile.companyPlaceholder')}
              />
            </Field>

            <Field label={t('profile.email')}>
              <Input
                type="email"
                value={email ?? ''}
                name="email"
                onChange={handleEmailChange}
                disabled={isReadonly}
                placeholder={t('profile.emailPlaceholder')}
              />
            </Field>

            <Field sx={{ gridColumn: '1 / -1' }} label={t('profile.bio')}>
              <Textarea
                value={bio ?? ''}
                name="bio"
                onChange={handleBioChange}
                disabled={isReadonly}
                placeholder={t('profile.bioPlaceholder')}
              />
            </Field>
          </Box>
        </Box>

        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          disabled={
            isReadonly || !firstName || !email || imageUploading || !hasChanges
          }
          loading={loading}
          onClick={handleSubmit}
        >
          {imageUploading ? t('profile.uploading') : t('common.update')}
        </Button>
      </Box>

      <Box
        component="section"
        aria-labelledby="change-password-title"
        sx={{
          mt: 5,
          pt: 3,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Text id="change-password-title" as="h2" variant="label">
            {t('nav.changePassword')}
          </Text>
          <Text variant="caption" tone="muted">
            {t('profile.changePasswordHint')}
          </Text>
        </Box>
        <Button
          type="button"
          variant="outlined"
          size="sm"
          onClick={() => setChangePasswordOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          {t('nav.changePassword')}
        </Button>
      </Box>

      {userDetails.data?.user?.firstName ? (
        <Box
          component="section"
          aria-labelledby="delete-account-title"
          sx={{
            mt: 3,
            pt: 3,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Text id="delete-account-title" as="h2" variant="label">
              {t('profile.deleteAccount')}
            </Text>
            <Text variant="caption" tone="muted">
              {t('profile.deleteAccountHint')}
            </Text>
          </Box>
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={handleDeleteAccount}
            sx={{ flexShrink: 0 }}
          >
            {t('profile.deleteAccount')}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default ProfileInformation;
